import { useState } from 'react';
import { cx, ErrorState, Spinner } from '@/components/ui';
import { formatKrw, formatPercent } from '@/lib/format';
import { HelpButton } from './HelpButton';
import { kstDateString } from './kstDate';
import type { IndicatorHelp } from './indicatorHelp';
import type { LadderGuide, LadderRange, LadderRow } from './stockTypes';
import { useLadderGuide, usePriceAlertMutations, usePriceAlerts } from './useStocks';

/**
 * 한 종목에 걸 수 있는 가격 알림 수 - StockDetailModal.MAX_ALERTS_PER_STOCK · 백엔드 PriceAlertService.MAX_PER_STOCK 과 같다.
 * 🔴 셋이 갈리면 서버가 409(T011)로 거절하는 것을 화면이 허용한다
 */
const MAX_ALERTS_PER_STOCK = 5;

/**
 * 종목 모달 "저점매수 사다리" 칸. (2026-10-05 사용자 결정, BACKLOG I-89 · docs/impl/138 18.8)
 *
 * 차트와 기술적 지표 사이에 놓는다(사용자 지정). 3 · 6개월 범위의 하위 30 · 20 · 10 을 1단으로 하는 사다리를 줄마다 보여 주고,
 * 줄의 [알림] 을 누르면 그 가격들에 "저점 ↓" 가격 알림을 건다. 걸린 알림은 기존처럼 관심종목 설정의 가격 알림 목록에 나온다.
 *
 * 🔴 고르지 않는다 - 알림은 사용자가 [알림] 을 눌러야만 걸린다. ★ 는 "기본 규칙" 줄 표시일 뿐이다(사용자 10/5 확인)
 * 🔴 숫자(기간 · 하위 비율 · 간격)는 서버 설정(trading.yml ladder-guide)에서 받아 그린다(CLAUDE.md 6절 9항)
 * 🔴 이미 지난 가격(현재가 ≤ 기준가)에는 걸지 않는다 - 걸자마자 15초 안에 울린다
 * 🔴 관심종목에 없는 종목은 서버가 거절한다(T010 - 시세 폴링 대상이 관심종목 합집합이라서). 버튼을 막고 이유를 보여 준다
 */
export function LadderGuideSection({
  stockId,
  currentPrice,
  watched,
}: {
  stockId: number;
  /** 실시간 현재가. 없으면 마지막 종가로 "지남" 을 판정한다 */
  currentPrice: number | null;
  watched: boolean;
}) {
  const guide = useLadderGuide(stockId);

  return (
    <section className="border-t border-line px-4 py-4">
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-sm font-semibold text-fg">저점매수 사다리</h3>
        {guide.data?.available && <HelpButton help={helpOf(guide.data)} />}
      </div>

      {guide.isLoading && <Spinner label="사다리를 계산하는 중" />}
      {guide.isError && (
        <ErrorState
          message={guide.error?.message ?? '사다리를 불러오지 못했습니다.'}
          onRetry={() => void guide.refetch()}
        />
      )}
      {guide.data && !guide.data.available && (
        <p className="text-sm text-fg-muted">{guide.data.reason}</p>
      )}
      {guide.data?.available && (
        <LadderTable
          stockId={stockId}
          guide={guide.data}
          price={currentPrice ?? guide.data.closePrice}
          watched={watched}
        />
      )}
    </section>
  );
}

function LadderTable({
  stockId,
  guide,
  price,
  watched,
}: {
  stockId: number;
  guide: LadderGuide;
  price: number | null;
  watched: boolean;
}) {
  const alerts = usePriceAlerts();
  const mine = (alerts.data ?? []).filter((entry) => entry.stockId === stockId);

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-fg-muted">
        현재가 {formatKrw(price)} · 간격 {guide.spacingAtr} ATR = {formatKrw(guide.rungStep)} ·
        {' '}{guide.baseDate} 일봉까지로 계산
        {/*
          오늘 일봉이 들어 있으면 잠정값이다 - 일봉은 저녁 확정 배치 전까지 바뀐다(2026-09-23 기준가 사고와 같은 성질).
          baseDate 만 보여 주면 "오늘 자료가 완성됐다" 로 읽힌다(B 점검 10/5)
        */}
        {guide.baseDate === kstDateString() && ' (오늘 일봉은 저녁 확정 전 잠정값이라 밤에 조금 바뀔 수 있습니다)'}
      </p>
      {guide.ranges.map((range) => {
        const position = livePosition(range, price);
        return (
        <div key={range.days} className="flex flex-col gap-1.5">
          <p className="text-xs text-fg-subtle">
            {periodLabel(range.days)} 범위 {formatKrw(range.low)} ~ {formatKrw(range.high)}
            {position !== null && ` · 지금 ${position.toFixed(1)}% 지점`}
          </p>
          {range.rows.map((row) => (
            <LadderRowLine
              key={row.heightPct}
              stockId={stockId}
              row={row}
              price={price}
              watched={watched}
              mine={mine}
            />
          ))}
          {/*
            범위 아래 경고 (2026-10-05 사용자 요청 - "0보다 아래, 즉 - 로 가면 하위 10 아래에 색을 구분해 문구")
              현재가 < 범위 최저 = 위치가 음수 = 그 기간의 신저가 구간이다.
              🔴 색은 주황(배지 warning 쌍)이다 - 빨강 · 파랑은 이 앱에서 가격 방향이라 쓰지 않는다. 다크 모드 쌍은 index.css 에 있다
              🔴 장중에만 생긴다 - 범위는 마지막 일봉까지라, 오늘 저가가 일봉으로 들어오면 최저가 내려가 위치가 다시 0 이상이 된다
          */}
          {position !== null && position < 0 && (
            <p className="rounded-md bg-badge-warning px-2 py-1.5 text-[11px] leading-snug text-badge-warning-fg">
              ⚠ 범위 아래 — 현재가가 {periodLabel(range.days)} 최저 {formatKrw(range.low)}보다{' '}
              {formatPercent(((price as number) - range.low) / range.low * 100)} 낮은 신저가 구간입니다(범위의{' '}
              {position.toFixed(1)}% 지점). 이 기간 사다리는 1단이 모두 지났고, 현재가 아래에 남은 단만 알림을 걸 수
              있습니다. 오늘 일봉이 들어오면 범위 최저와 기준가가 함께 내려갑니다.
            </p>
          )}
        </div>
        );
      })}
      {/*
        알림은 건 날 가격으로 고정되지만 사다리는 매일 다시 계산된다(범위가 하루씩 밀린다).
        B 실측(10/5 · 대형 6종목 · 2024-06~ · 6개월 하위 30): 5거래일 안 이동 중앙 0% · 20거래일 뒤 중앙 3.6% · 10%는 16% 이상 → 138번 19.6
      */}
      <p className="text-xs text-fg-subtle">
        가격은 매일 다시 계산됩니다. 걸어 둔 알림은 건 날 가격 그대로라, 오래 안 닿은 알림은 오늘 사다리와 다를 수 있습니다.
      </p>
      {!watched && (
        <p className="text-xs text-fg-subtle">
          알림은 관심종목에 담은 종목에만 걸 수 있습니다(시세를 관심종목만 받아서입니다).
        </p>
      )}
    </div>
  );
}

function LadderRowLine({
  stockId,
  row,
  price,
  watched,
  mine,
}: {
  stockId: number;
  row: LadderRow;
  price: number | null;
  watched: boolean;
  mine: { direction: string; targetPrice: number }[];
}) {
  const { set } = usePriceAlertMutations();
  const [state, setState] = useState<{ done: number; error: string | null } | null>(null);

  // 이미 지난 단(현재가가 그 아래거나 같다) - 걸면 바로 울린다
  const passed = (target: number) => price !== null && price <= target;
  // 걸 가격 = 안 지난 단 중 같은 값 하나만(간격 0 이면 세 단이 같다) · 이미 걸린 같은 "저점 ↓" 는 서버가 다시 켠다(자리 안 씀)
  const targets = [...new Set(row.rungs.filter((p) => !passed(p)))];
  const already = (p: number) => mine.some((a) => a.direction === 'BELOW' && a.targetPrice === p);
  const newSlots = targets.filter((p) => !already(p)).length;
  const full = mine.length + newSlots > MAX_ALERTS_PER_STOCK;

  const disabledReason = !watched
    ? '관심종목에 담아야 걸 수 있습니다'
    : targets.length === 0
      ? '모든 단이 이미 지났습니다'
      : full
        ? `알림 자리가 모자랍니다(지금 ${mine.length}/${MAX_ALERTS_PER_STOCK} · 새로 ${newSlots}개 필요)`
        : null;

  const apply = async () => {
    setState({ done: 0, error: null });
    // 차례로 건다 - 한꺼번에 보내면 상한 검사가 서로를 못 봐 6번째가 들어갈 수 있다(서버는 막지만 오류가 섞여 나온다)
    for (let i = 0; i < targets.length; i++) {
      try {
        await set.mutateAsync({ stockId, targetPrice: targets[i] as number, direction: 'BELOW' });
        setState({ done: i + 1, error: null });
      } catch (e) {
        setState({ done: i, error: e instanceof Error ? e.message : '알림을 걸지 못했습니다' });
        return;
      }
    }
  };

  const first = row.rungs[0];
  return (
    <div className="flex flex-col gap-1 rounded-md bg-surface-hover px-2 py-1.5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="w-16 shrink-0 text-xs font-medium text-fg">
          하위 {row.heightPct}
          {row.base && <span title="기본 규칙(표시일 뿐 추천이 아닙니다)"> ★</span>}
        </span>
        <span className="flex flex-1 flex-wrap gap-x-2 text-xs tabular-nums">
          {row.rungs.map((p, k) => (
            <span
              key={k}
              className={cx(passed(p) ? 'text-fg-subtle line-through' : 'text-fg')}
              title={passed(p) ? '현재가가 이미 이 아래라 알림에서 뺍니다' : undefined}
            >
              {k + 1}단 {formatKrw(p)}
            </span>
          ))}
        </span>
        <button
          type="button"
          onClick={() => void apply()}
          disabled={disabledReason !== null || set.isPending}
          title={disabledReason ?? `이 줄의 ${targets.length}개 가격에 저점 알림을 겁니다`}
          className="shrink-0 rounded-full bg-brand px-3 py-1 text-xs font-medium text-brand-fg disabled:cursor-not-allowed disabled:opacity-40"
        >
          알림 {targets.length}개
        </button>
      </div>
      {first !== undefined && price !== null && price > 0 && (
        <p className="text-[11px] text-fg-subtle">
          1단은 현재가 대비 {formatPercent(((first - price) / price) * 100)}
          {watched && disabledReason !== null && ` · ${disabledReason}`}
        </p>
      )}
      {state && state.error === null && state.done === targets.length && (
        <p className="text-[11px] text-fg-muted">✓ 알림 {state.done}개를 걸었습니다 · 위 가격 알림 목록에서 지울 수 있습니다</p>
      )}
      {state?.error && (
        <p className="text-[11px] text-danger">
          {state.done}개를 건 뒤 멈췄습니다 — {state.error}
        </p>
      )}
    </div>
  );
}

/**
 * 현재가가 범위의 몇 % 지점인가. 범위 최저 아래면 음수다(신저가 구간).
 *
 * 서버의 positionPct 는 기준일 종가로 잰 값이라 장중 움직임을 모른다. 칸 머리의 "현재가" 와 같은 값으로 다시 잰다 -
 * 같은 줄에 현재가와 다른 가격으로 잰 위치가 나오면 어느 쪽을 믿을지 모른다. 현재가가 없으면 서버 값을 쓴다.
 * 최고 = 최저(거래정지 등)면 null.
 */
function livePosition(range: LadderRange, price: number | null): number | null {
  const width = range.high - range.low;
  if (width <= 0) {
    return null;
  }
  if (price === null) {
    return range.positionPct;
  }
  return ((price - range.low) / width) * 100;
}

/** 60 → "3개월(60거래일)". 거래일은 한 달에 약 20일이라 20 으로 나눠 부른다 */
function periodLabel(days: number): string {
  return `${Math.round(days / 20)}개월(${days}거래일)`;
}

/**
 * ? 도움말. 기간 · 하위 비율 · 간격 · ATR 은 응답에서 받아 쓴다(설정이 바뀌면 문장도 따라 바뀐다).
 *
 * 🔴 측정 문장(2015~2026 · 129개 구간 · ±4%p · 80%)은 docs/impl/138 18.8 결과다 - 설정과 달리 측정을 다시 하면 손으로 고친다
 */
function helpOf(guide: LadderGuide): IndicatorHelp {
  const periods = guide.ranges.map((r) => periodLabel(r.days)).join(' · ');
  const heights = guide.ranges[0]?.rows.map((r) => r.heightPct).join(' · ') ?? '';
  const baseRange = guide.ranges.find((r) => r.rows.some((row) => row.base));
  const baseRow = baseRange?.rows.find((row) => row.base);
  return {
    label: '저점매수 사다리',
    hint: '범위 하위 지점에서 나눠 사는 계획표',
    detail: [
      `최근 ${periods} 동안의 저가~고가를 0~100 으로 보고, 하위 ${heights} 지점을 1단으로 잡았습니다. ` +
        `2단 · 3단은 그 아래로 ATR(하루 평균 변동폭, 지금 ${formatKrw(guide.atr14)})의 ${guide.spacingAtr}배씩 내려갑니다.`,
      `[알림] 을 누르면 그 줄의 가격에 "저점 ↓" 가격 알림을 겁니다. 걸린 알림은 관심종목 설정의 가격 알림 목록에 나오고 거기서 지웁니다. ` +
        `현재가가 이미 그 아래인 단(줄 그은 가격)은 걸자마자 울리므로 빼고 겁니다. 한 종목에 ${MAX_ALERTS_PER_STOCK}개까지입니다.`,
      baseRow && baseRange
        ? `★ 는 기본 규칙(${periodLabel(baseRange.days)} 하위 ${baseRow.heightPct})입니다. 표시일 뿐 추천이나 자동 선택이 아닙니다.`
        : '★ 는 기본 규칙 표시입니다.',
      '과거 재현(2015~2026년 6개월 구간 129개 · 손절 없이 평단 +5% 에 파는 사다리)에서 이 기간 · 높이들은 서로 가릴 수 없을 만큼 비슷했습니다' +
        '(6개월 하위 30 대비 같은 구간 수익 차이 중앙 ±4%p 안). 3개월 하위 20 이 가장 높았지만(+2.7%p · 129개 중 79개에서 나음) 우연과 구분되지 않는 크기입니다. ' +
        '12개월 범위는 대체로 가장 낮아(12개월 하위 10 이 아홉 자리 중 최저) 넣지 않았습니다. 어느 자리에서 시작하든 6개월 끝에 원금의 약 80% 가 아직 묶여 있었습니다.',
      `간격 ${guide.spacingAtr}배는 과거 10년 재현에서 0.7배보다 나았고, 최근 6개월 단위로는 가릴 수 없었던 임시 기본값입니다. 측정으로 고른 최적값이 아닙니다.`,
      '알아 둘 한계 — ① 사다리는 매일 다시 계산되지만 걸어 둔 알림은 건 날 가격 그대로입니다. 대형주 여섯 종목을 재 보니 6개월 하위 30 자리가 5거래일 안에는 대개 그대로였고, 20거래일 뒤에는 중앙 3.6%(열에 하나는 16% 넘게) 움직였습니다. 오래 걸어 둔 알림은 다시 확인하세요. ' +
        '② 2단 · 3단은 1단이 계획한 가격에 산다고 보고 미리 잡은 가격입니다. 장 시작부터 크게 빠져 1단을 더 싸게 사면, 그 가격에서 다시 잡는 편이 계획과 맞습니다. ' +
        '③ 분할 · 거래정지 같은 이벤트가 일봉에 반영되지 않은 종목은 범위가 틀릴 수 있습니다. ' +
        '④ 상장한 지 그 기간이 안 된 종목은 그 기간 줄이 나오지 않습니다.',
      '이 숫자들은 일봉(시간외 포함 통합 봉)으로 계산한 값이며 AI가 만든 값이 아닙니다. 주문하지 않습니다.',
    ],
  };
}
